import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Shield, ExternalLink, MapPin, Route, Lightbulb, Lock, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gumroad links - will be populated when user provides them
const GUMROAD_LINKS = {
  iceland: '',
  japan: '',
  new_zealand: '',
  sicily: ''
};

const GuideDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [guide, setGuide] = useState(null);
  const [guideContent, setGuideContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gumroadUrl, setGumroadUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [guideRes, contentRes, gumroadRes] = await Promise.all([
          axios.get(`${API}/guides/${id}`),
          axios.get(`${API}/guide-content/${id}`),
          axios.get(`${API}/gumroad/link/${id}`).catch(() => ({ data: { gumroad_url: '' } }))
        ]);
        setGuide(guideRes.data);
        setGuideContent(contentRes.data);
        setGumroadUrl(gumroadRes.data.gumroad_url || '');
      } catch (error) {
        console.error('Error fetching guide:', error);
        navigate('/guides');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handlePurchase = () => {
    if (gumroadUrl) {
      window.open(gumroadUrl, '_blank');
    } else {
      // Fallback: map guide title to Gumroad link
      const titleKey = guide?.title_en?.toLowerCase().replace(' ', '_');
      const fallbackUrl = GUMROAD_LINKS[titleKey];
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank');
      } else {
        alert(language === 'it' 
          ? 'Link di acquisto non ancora disponibile. Riprova più tardi.' 
          : 'Purchase link not yet available. Please try again later.');
      }
    }
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-[#1C1C1E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-[#2C2C2E] rounded w-24 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="h-[500px] bg-[#2C2C2E] rounded-3xl" />
              <div className="space-y-4">
                <div className="h-10 bg-[#2C2C2E] rounded w-3/4" />
                <div className="h-6 bg-[#2C2C2E] rounded w-1/2" />
                <div className="h-32 bg-[#2C2C2E] rounded" />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!guide) return null;

  const title = language === 'it' ? guide.title_it : guide.title_en;
  const description = language === 'it' ? guide.description_it : guide.description_en;

  return (
    <main className="pt-20 min-h-screen bg-[#1C1C1E]" data-testid="guide-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/guides')}
          className="flex items-center gap-2 text-[#86868B] hover:text-[#F5F5F7] transition-colors mb-8"
          data-testid="back-to-guides"
        >
          <ArrowLeft className="w-5 h-5" />
          {t.nav.guides}
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl">
              <img
                src={guide.image_url}
                alt={title}
                className="w-full h-[400px] lg:h-[600px] object-cover"
              />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col"
          >
            <span className="text-[#C9B896] text-sm font-medium uppercase tracking-wider mb-4">
              {guide.category}
            </span>
            
            <h1 className="font-['Manrope'] font-bold tracking-tight text-4xl md:text-5xl mb-4 text-white">
              {title}
            </h1>

            <p className="font-['Inter'] text-lg text-[#86868B] leading-relaxed mb-8">
              {description}
            </p>

            {/* What's Inside */}
            <div className="bg-[#2C2C2E] rounded-2xl p-6 md:p-8 mb-8 border border-[#3A3A3C]">
              <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-xl mb-4">
                {t.guideDetail.whatsInside}
              </h3>
              <ul className="space-y-3">
                {t.guideDetail.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#C9B896] mt-0.5 flex-shrink-0" />
                    <span className="text-[#F5F5F7]">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Price & Purchase */}
            <div className="mt-auto space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[#86868B]">{t.guides.from}</span>
                <span className="font-['Manrope'] font-bold text-[#F5F5F7] text-4xl">
                  €{guide.price.toFixed(2)}
                </span>
              </div>

              <Button
                onClick={handlePurchase}
                className="w-full h-14 bg-[#C9B896] hover:bg-[#A89770] text-white text-lg font-medium rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                data-testid="purchase-btn"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                {t.guideDetail.buyGuide}
              </Button>

              <div className="flex items-center justify-center gap-2 text-[#86868B] text-sm">
                <Shield className="w-4 h-4" />
                {t.guideDetail.secure}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Content Preview Section */}
        {guideContent && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 pt-16 border-t border-[#2C2C2E]"
          >
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-12">
              <div className="bg-[#2C2C2E] rounded-2xl p-6 text-center border border-[#3A3A3C]">
                <MapPin className="w-8 h-8 text-[#C9B896] mx-auto mb-2" />
                <div className="font-['Manrope'] font-bold text-3xl text-[#F5F5F7]">
                  {guideContent.places_count}
                </div>
                <div className="text-[#86868B] text-sm">
                  {language === 'it' ? 'Luoghi' : 'Places'}
                </div>
              </div>
              <div className="bg-[#2C2C2E] rounded-2xl p-6 text-center border border-[#3A3A3C]">
                <Route className="w-8 h-8 text-[#C9B896] mx-auto mb-2" />
                <div className="font-['Manrope'] font-bold text-3xl text-[#F5F5F7]">
                  {guideContent.itineraries_count}
                </div>
                <div className="text-[#86868B] text-sm">
                  {language === 'it' ? 'Itinerari' : 'Itineraries'}
                </div>
              </div>
              <div className="bg-[#2C2C2E] rounded-2xl p-6 text-center border border-[#3A3A3C]">
                <Lightbulb className="w-8 h-8 text-[#C9B896] mx-auto mb-2" />
                <div className="font-['Manrope'] font-bold text-3xl text-[#F5F5F7]">
                  {guideContent.tips_count}
                </div>
                <div className="text-[#86868B] text-sm">Tips</div>
              </div>
            </div>

            {/* Preview Header */}
            <div className="flex items-center gap-3 mb-8">
              <Eye className="w-6 h-6 text-[#C9B896]" />
              <h2 className="font-['Manrope'] font-semibold text-2xl text-[#F5F5F7]">
                {language === 'it' ? 'Anteprima Contenuto' : 'Content Preview'}
              </h2>
            </div>

            {/* Preview Places */}
            {guideContent.preview_places && guideContent.preview_places.length > 0 && (
              <div className="mb-12">
                <h3 className="font-['Manrope'] font-medium text-lg text-[#F5F5F7] mb-4">
                  {language === 'it' ? 'Alcuni luoghi inclusi' : 'Some places included'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {guideContent.preview_places.map((place, index) => (
                    <motion.div
                      key={place.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="bg-[#2C2C2E] rounded-2xl overflow-hidden border border-[#3A3A3C] hover:border-[#C9B896]/50 transition-colors"
                    >
                      <div className="relative h-48">
                        <img
                          src={place.image_url}
                          alt={language === 'it' ? place.name_it : place.name_en}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-[#2C2C2E]/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-[#C9B896]">
                          {place.category}
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-['Manrope'] font-semibold text-[#F5F5F7] mb-2">
                          {language === 'it' ? place.name_it : place.name_en}
                        </h4>
                        <p className="text-sm text-[#86868B] line-clamp-2">
                          {language === 'it' ? place.description_it : place.description_en}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* More places locked */}
                <div className="mt-6 flex items-center justify-center gap-2 text-[#86868B]">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">
                    {language === 'it' 
                      ? `+ ${guideContent.places_count - guideContent.preview_places.length} altri luoghi nella guida completa`
                      : `+ ${guideContent.places_count - guideContent.preview_places.length} more places in the full guide`
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Preview Tips */}
            {guideContent.preview_tips && guideContent.preview_tips.length > 0 && (
              <div className="mb-12">
                <h3 className="font-['Manrope'] font-medium text-lg text-[#F5F5F7] mb-4">
                  {language === 'it' ? 'Alcuni consigli inclusi' : 'Some tips included'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {guideContent.preview_tips.map((tip, index) => (
                    <motion.div
                      key={tip.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="bg-[#2C2C2E] rounded-xl p-5 border border-[#3A3A3C]"
                    >
                      <h4 className="font-['Manrope'] font-semibold text-[#F5F5F7] mb-2">
                        {language === 'it' ? tip.title_it : tip.title_en}
                      </h4>
                      <p className="text-sm text-[#86868B] line-clamp-3">
                        {language === 'it' ? tip.content_it : tip.content_en}
                      </p>
                    </motion.div>
                  ))}
                </div>
                
                {/* More tips locked */}
                <div className="mt-6 flex items-center justify-center gap-2 text-[#86868B]">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">
                    {language === 'it' 
                      ? `+ ${guideContent.tips_count - guideContent.preview_tips.length} altri consigli nella guida completa`
                      : `+ ${guideContent.tips_count - guideContent.preview_tips.length} more tips in the full guide`
                    }
                  </span>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#C9B896] to-[#00C7FF] rounded-2xl p-8 text-center text-white">
              <h3 className="font-['Manrope'] font-bold text-2xl mb-2">
                {language === 'it' ? 'Sblocca la Guida Completa' : 'Unlock the Full Guide'}
              </h3>
              <p className="opacity-90 mb-6">
                {language === 'it' 
                  ? `Accedi a tutti i ${guideContent.places_count} luoghi, ${guideContent.itineraries_count} itinerari e ${guideContent.tips_count} consigli`
                  : `Access all ${guideContent.places_count} places, ${guideContent.itineraries_count} itineraries and ${guideContent.tips_count} tips`
                }
              </p>
              <div className="font-['Manrope'] font-bold text-4xl">
                €{guide.price.toFixed(2)}
              </div>
            </div>
          </motion.section>
        )}
      </div>
    </main>
  );
};

export default GuideDetail;
